// Admin: list and filter error reports (worker + frontend) stored in Supabase error_reports

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { ChevronLeft, ChevronRight, Copy } from 'lucide-react';

const PAGE_SIZE = 50;

type ErrorReport = {
  id: string;
  level: 'error' | 'warn' | 'info';
  source: 'worker' | 'frontend';
  message: string;
  stack: string | null;
  context: Record<string, unknown> | null;
  created_at: string;
};

export default function AdminErrorReports() {
  const [page, setPage] = useState(1);
  const [level, setLevel] = useState<string>('all');
  const [source, setSource] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [detail, setDetail] = useState<ErrorReport | null>(null);
  const { toast } = useToast();

  const copyReportToClipboard = async (report: ErrorReport) => {
    const parts = [
      `[${report.created_at}] ${report.level} · ${report.source}`,
      '',
      'Mensaje:',
      report.message,
      ...(report.stack ? ['', 'Stack:', report.stack] : []),
      ...(report.context != null && Object.keys(report.context).length > 0
        ? ['', 'Contexto:', JSON.stringify(report.context, null, 2)]
        : []),
    ];
    const text = parts.join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: 'Copiado', description: 'Reporte copiado al portapapeles.' });
    } catch {
      toast({ title: 'Error', description: 'No se pudo copiar.', variant: 'destructive' });
    }
  };

  const { data, isLoading } = useQuery({
    queryKey: ['store', 'error_reports', page, level, source, dateFrom, dateTo],
    queryFn: async (): Promise<{ rows: ErrorReport[]; total: number }> => {
      let q = supabase
        .from('error_reports')
        .select('id,level,source,message,stack,context,created_at', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

      if (level !== 'all') q = q.eq('level', level);
      if (source !== 'all') q = q.eq('source', source);
      if (dateFrom) q = q.gte('created_at', `${dateFrom}T00:00:00.000Z`);
      if (dateTo) q = q.lte('created_at', `${dateTo}T23:59:59.999Z`);

      const { data: rows, error, count } = await q;
      if (error) throw error;
      return { rows: (rows ?? []) as ErrorReport[], total: count ?? 0 };
    },
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const reports = data?.rows ?? [];

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Reportes de errores</h1>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4 mb-6">
        <div className="space-y-2">
          <Label className="text-xs">Nivel</Label>
          <Select value={level} onValueChange={(v) => { setLevel(v); setPage(1); }}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="error">error</SelectItem>
              <SelectItem value="warn">warn</SelectItem>
              <SelectItem value="info">info</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-xs">Origen</Label>
          <Select value={source} onValueChange={(v) => { setSource(v); setPage(1); }}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="worker">worker</SelectItem>
              <SelectItem value="frontend">frontend</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-xs">Desde (fecha)</Label>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            className="w-[140px]"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs">Hasta (fecha)</Label>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            className="w-[140px]"
          />
        </div>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Cargando…</p>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[160px]">Fecha</TableHead>
                <TableHead className="w-[80px]">Nivel</TableHead>
                <TableHead className="w-[90px]">Origen</TableHead>
                <TableHead>Mensaje</TableHead>
                <TableHead className="w-[100px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-muted-foreground text-center py-8">
                    No hay registros con los filtros actuales.
                  </TableCell>
                </TableRow>
              ) : (
                reports.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
                      {new Date(r.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Badge variant={r.level === 'error' ? 'destructive' : r.level === 'warn' ? 'secondary' : 'outline'}>
                        {r.level}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{r.source}</TableCell>
                    <TableCell className="max-w-[400px]">
                      <span title={r.message} className="line-clamp-2 text-sm">
                        {r.message}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => setDetail(r)}>
                        Ver detalle
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {data && data.total > PAGE_SIZE && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, data.total)} de {data.total}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex flex-row items-center justify-between space-y-0">
            <DialogTitle>Detalle del reporte</DialogTitle>
            {detail && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyReportToClipboard(detail)}
                className="shrink-0"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copiar
              </Button>
            )}
          </DialogHeader>
          {detail && (
            <div className="space-y-4 overflow-y-auto pr-2">
              <div>
                <Label className="text-xs text-muted-foreground">Mensaje</Label>
                <p className="text-sm mt-1 break-words">{detail.message}</p>
              </div>
              {detail.stack && (
                <div>
                  <Label className="text-xs text-muted-foreground">Stack</Label>
                  <pre className="mt-1 text-xs bg-muted p-3 rounded-md overflow-x-auto whitespace-pre-wrap break-words max-h-[200px] overflow-y-auto">
                    {detail.stack}
                  </pre>
                </div>
              )}
              {detail.context != null && Object.keys(detail.context).length > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground">Contexto</Label>
                  <pre className="mt-1 text-xs bg-muted p-3 rounded-md overflow-x-auto whitespace-pre-wrap break-words max-h-[200px] overflow-y-auto">
                    {JSON.stringify(detail.context, null, 2)}
                  </pre>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                {detail.created_at} · {detail.level} · {detail.source}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
